"use client";


import React, { useEffect, useState } from "react";
import { client } from "../../sanity/lib/client";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import Link from "next/link";
import { NormalizeUrl } from "@/utils/normalizeUrl";
import Image from "next/image";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import {VeicoloData} from "@/components/VeicoloView/VeicoloView";

interface SectionPageProps {
    title: string;
    slug: string;
    categories: Array<{
        _id: string;
        title: string;
        slug: string;
        faqs: Array<{
            _id: string;
            question: string;
            answer: string;
            slug: string;
        }> | null;
    }>;
}

interface ToolbarVeicoloaProps {
    veicolo: VeicoloData;
}

export default function ToolbarVeicolo({veicolo} : ToolbarVeicoloaProps ) {
    const [section, setSection] = useState<SectionPageProps>();

    const schedaTecnica = [
        { label: "Cilindrata", value: veicolo.cilindrata || "N/A" },
        { label: "KW", value: veicolo.kw },
        { label: "CV", value: veicolo.cv },
        { label: "Velocità Max", value: veicolo.velocitaMax || "N/A" },
        { label: "Accelerazione (0-100 km/h)", value: veicolo.accelerazione || "N/A" },
        { label: "Coppia", value: veicolo.coppia || "N/A" },
        { label: "Trazione", value: veicolo.trazione },
        { label: "Pneumatici", value: veicolo.pneumatici },
        { label: "Lunghezza", value: veicolo.lunghezza },
        { label: "Larghezza", value: veicolo.larghezza },
        { label: "Altezza", value: veicolo.altezza },
        { label: "Passo", value: veicolo.passo },
        { label: "Numero Porte", value: veicolo.porte },
        { label: "Numero Airbag", value: veicolo.numeroAirbag || "N/A" },
        { label: "Volume Bagagliaio", value: veicolo.volumeBagagliaio || "N/A" },
        { label: "Numero Marce", value: veicolo.numeroMarce || "N/A" },
        { label: "Motore", value: veicolo.motore },
        { label: "Immatricolazione", value: new Date(veicolo.immatricolazione).toLocaleDateString() },
        { label: "Chilometraggio", value: `${veicolo.km} km` },
    ];

    const serviziInclusi = [
        "Copertura assicurativa RCA con franchigia euro 500",
        "Furto parziale scoperto 10% del danno subito con franchigia euro 500",
        "Furto totale ed incendio scoperto 10% valore eurotax giallo",
        "Soccorso stradale e Gestione sinistri",
        //"Canone antifurto satellitare",
    ];

    const validAccessori = Array.isArray(veicolo.accessori) ? veicolo.accessori : [];


    useEffect(() => {
        const fetchFAQs = async () => {
            const section = await client.fetch(
                `*[_type == "section" && slug.current == $sectionSlug][0] {
                    title,
                    "slug": slug.current,
                    "categories": *[_type == "category" && references(^._id)] | order(orderRank) {
                      _id,
                      title,
                      "slug": slug.current,
                      "faqs": *[_type == "faq" && references(^._id)] | order(orderRank) {
                        _id,
                        question,
                        answer,
                        "slug": slug.current
                      }
                    }
                  }`,
                { sectionSlug: "rent-to-buy" }
            );


            setSection(section);
        };

        fetchFAQs();
    }, []);

    return (
        <section id="details" className="my-24">
            <h5 className="text-4xl font-normal m-3">Dettagli</h5>
            <Tabs defaultValue="optional" className="w-full">
                <TabsList className="flex w-full justify-start max-sm:flex-col max-sm:h-auto">
                    <TabsTrigger
                        value="optional"
                        className="w-1/5 data-[state=active]:bg-primary data-[state=active]:text-white max-sm:w-full "
                    >
                        OPTIONAL
                    </TabsTrigger>
                    <TabsTrigger
                        value="schedaTecnica"
                        className="w-1/5 data-[state=active]:bg-primary max-sm:w-full data-[state=active]:text-white"
                    >
                        SCHEDA TECNICA
                    </TabsTrigger>
                    <TabsTrigger
                        value="serviziInclusi"
                        className="w-1/5 data-[state=active]:bg-primary max-sm:w-full data-[state=active]:text-white"
                    >
                        SERVIZI INCLUSI
                    </TabsTrigger>
                    <TabsTrigger
                        value="comeFunziona"
                        className="w-1/5 data-[state=active]:bg-primary max-sm:w-full data-[state=active]:text-white"
                    >
                        COME FUNZIONA
                    </TabsTrigger>
                </TabsList>


                <TabsContent value="optional">
                    <ul id="lista-accessori" className="bg-[#F4F4F4] p-4 grid grid-cols-2 max-sm:grid-cols-1 gap-x-20 overflow-y-auto max-h-[500px]">
                        {validAccessori.length > 0 ? (
                            validAccessori.map((item: string, index) => (
                                <li key={index} className="flex justify-between my-2">
                                    <p className="text-left max-w-[70%]">{item?.trim()}</p>
                                </li>
                            ))
                        ) : (
                            <p>Nessun accessorio disponibile</p>
                        )}
                    </ul>
                </TabsContent>

                <TabsContent value="schedaTecnica">
                    <ul id="lista-accessori" className="bg-[#F4F4F4] p-4 grid grid-cols-2 max-sm:grid-cols-1 gap-x-20 overflow-y-auto max-h-[500px]">
                        {schedaTecnica.map((item, index) => (
                            <li key={index} className="flex justify-between my-2">
                                <p className="text-[#757575]">{item.label}</p>
                                <p className="text-right max-w-[70%]">{item.value}</p>
                            </li>
                        ))}
                    </ul>
                </TabsContent>

                <TabsContent value="serviziInclusi">
                    <ul className="bg-[#F4F4F4] p-4">
                        {serviziInclusi.map((servizio, index) => (
                            <li key={index} className="my-2">
                                {servizio}
                            </li>
                        ))}
                    </ul>
                </TabsContent>

                <TabsContent value="comeFunziona">
                    <div className="bg-[#F4F4F4] p-4 relative w-full ">
                        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6 pt-0">
                            <Accordion type="single" collapsible className="w-full">
                                {section?.categories && section.categories.length > 0 ? (
                                    section.categories.map((category) => (
                                        <AccordionItem key={category._id} value={category._id}>
                                            <AccordionTrigger>{category.title}</AccordionTrigger>
                                            <AccordionContent>
                                                <div className="pb-4 pt-0">
                                                    <ul className="space-y-3">
                                                        {category.faqs &&
                                                        category.faqs.length > 0 ? (
                                                            category.faqs.map((faq) => (
                                                                <li key={faq._id}>
                                                                    <Link
                                                                        hrefLang="it"
                                                                        title={faq.question}
                                                                        href={NormalizeUrl(
                                                                            `/faq/${section.slug}/${category.slug}/${faq.slug}`
                                                                        )}
                                                                    >
                                                                        {faq.question}
                                                                    </Link>
                                                                </li>
                                                            ))
                                                        ) : (
                                                            <li>Nessuna sottocategoria disponibile</li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))
                                ) : (
                                    <div className="flex items-center justify-center flex-col gap-y-4">
                                        <p className="text-lg">Nessuna FAQ presente per questa sezione</p>
                                        <Image
                                            src={"https://www.consorzioauto.com/assets/search-card.svg"}
                                            alt={"No results found"}
                                            width={100}
                                            height={100}
                                        />
                                    </div>
                                )}
                            </Accordion>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </section>
    );
}

